/**
 * Resource Settings Panel — glassmorphism slide-out for resource configuration
 * and financial analysis parameters.
 *
 * Follows the SharePointSettings slide-out pattern.
 */

import { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useAdjacencyStore } from '../../store/adjacency-store';
import {
  DollarSign,
  X,
  Calculator,
  Users,
  RotateCcw,
  TrendingUp,
} from 'lucide-react';
import { computeResourceMetrics } from '../../types/resource-config';
import type { ResourceConfig } from '../../types/resource-config';

// ── Component ────────────────────────────────────────────────────────

export function ResourceSettings() {
  const {
    nodes,
    resourceConfig,
    updateResourceConfig,
    resetResourceConfig,
  } = useAdjacencyStore();

  const [isOpen, setIsOpen] = useState(false);

  // Lock body scroll when panel is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [isOpen]);

  // ── Computed metrics ────────────────────────────────────────────

  // Any node with iR&D or fR&D contributes — app is the source of truth
  const fundedNodes = useMemo(
    () => nodes.filter(n => ((n.iRnD ?? 0) + (n.fRnD ?? 0)) > 0),
    [nodes],
  );

  const grandTotal = useMemo(
    () => fundedNodes.reduce((sum, n) => sum + (n.iRnD ?? 0) + (n.fRnD ?? 0), 0),
    [fundedNodes],
  );

  const metrics = useMemo(
    () => computeResourceMetrics(resourceConfig, grandTotal),
    [resourceConfig, grandTotal],
  );

  // ── Config input handler ───────────────────────────────────────

  const handleConfigChange = (field: keyof ResourceConfig, value: string) => {
    const num = parseFloat(value);
    if (!isNaN(num)) {
      updateResourceConfig({ [field]: num });
    }
  };

  return (
    <>
      {/* ── Trigger button ─────────────────────────────────────── */}
      <button
        onClick={() => setIsOpen(true)}
        className="flex items-center gap-1.5 px-3 py-1.5 text-sm transition-colors rounded"
        style={{ color: 'var(--text-secondary)' }}
        title="Resource & Budget Settings"
      >
        <DollarSign size={16} />
        <span className="hidden sm:inline">Budget</span>
      </button>

      {/* ── Slide-out panel ────────────────────────────────────── */}
      {isOpen && createPortal(
        <div className="fixed inset-0 z-50 flex justify-end">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setIsOpen(false)}
          />

          <div
            className="relative w-full max-w-md flex flex-col"
            style={{
              background: 'var(--modal-bg-heavy)',
              backdropFilter: 'blur(24px) saturate(180%)',
              borderLeft: '1px solid var(--glass-border)',
            }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b"
              style={{ borderColor: 'var(--glass-border)' }}
            >
              <h2 className="text-base font-semibold flex items-center gap-2"
                style={{ color: 'var(--text-primary)' }}
              >
                <DollarSign size={18} />
                Resource & Budget Settings
              </h2>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1.5 rounded transition-colors hover:bg-white/10"
                style={{ color: 'var(--text-muted)' }}
              >
                <X size={18} />
              </button>
            </div>

            {/* Scrollable body */}
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">

              {/* ── Resource Configuration ─────────────────────── */}
              <Section title="Resource Parameters" icon={<Users size={14} />}>
                <div className="space-y-2.5">
                  <ConfigInput
                    label="Avg FTE Cost ($)"
                    value={resourceConfig.avgFteCost}
                    onChange={(v) => handleConfigChange('avgFteCost', v)}
                    format="currency"
                  />
                  <ConfigInput
                    label="IWT South Commitment ($)"
                    value={resourceConfig.iwtSouthCommitment}
                    onChange={(v) => handleConfigChange('iwtSouthCommitment', v)}
                    format="currency"
                  />
                  <ConfigInput
                    label="Other Subcontractor ($)"
                    value={resourceConfig.otherSubcontractorCommitment}
                    onChange={(v) => handleConfigChange('otherSubcontractorCommitment', v)}
                    format="currency"
                  />
                  <ConfigInput
                    label="FTE Efficiency (%)"
                    value={resourceConfig.fteEfficiency * 100}
                    onChange={(v) => handleConfigChange('fteEfficiency', String(parseFloat(v) / 100))}
                    format="percent"
                  />
                  <ConfigInput
                    label="2026 Revenue Projection ($)"
                    value={resourceConfig.revenueProjection}
                    onChange={(v) => handleConfigChange('revenueProjection', v)}
                    format="currency"
                  />
                  <ConfigInput
                    label="R&D % of Revenue"
                    value={resourceConfig.rndPercentOfRevenue * 100}
                    onChange={(v) => handleConfigChange('rndPercentOfRevenue', String(parseFloat(v) / 100))}
                    format="percent"
                  />
                  <ConfigInput
                    label="IWT Resources (FTEs)"
                    value={resourceConfig.iwtResources}
                    onChange={(v) => handleConfigChange('iwtResources', v)}
                    format="number"
                  />
                  <ConfigInput
                    label="Swim Lane Investment ($)"
                    value={resourceConfig.swimLaneInvestment}
                    onChange={(v) => handleConfigChange('swimLaneInvestment', v)}
                    format="currency"
                  />
                </div>

                <button
                  onClick={resetResourceConfig}
                  className="mt-3 flex items-center gap-1.5 px-3 py-1.5 text-xs rounded transition-colors"
                  style={{
                    color: 'var(--text-muted)',
                    border: '1px solid rgba(255,255,255,0.1)',
                  }}
                >
                  <RotateCcw size={12} />
                  Reset to Defaults
                </button>
              </Section>

              {/* ── NPV Parameters ──────────────────────────── */}
              <Section title="NPV Parameters" icon={<TrendingUp size={14} />}>
                <div className="space-y-2.5">
                  <ConfigInput
                    label="Discount Rate (%)"
                    value={resourceConfig.npvDiscountRate * 100}
                    onChange={(v) => handleConfigChange('npvDiscountRate', String(parseFloat(v) / 100))}
                    format="percent"
                  />
                  <div className="flex items-center justify-between gap-3">
                    <label className="text-xs shrink-0" style={{ color: 'var(--text-secondary)' }}>
                      Analysis Period
                    </label>
                    <select
                      value={resourceConfig.npvPeriodYears}
                      onChange={(e) => updateResourceConfig({ npvPeriodYears: parseInt(e.target.value) })}
                      className="w-32 px-2 py-1 text-xs text-right rounded"
                      style={{
                        background: 'var(--modal-input-bg)',
                        border: '1px solid var(--modal-input-border)',
                        color: 'var(--text-primary)',
                        outline: 'none',
                      }}
                    >
                      <option value={3}>3 years</option>
                      <option value={5}>5 years</option>
                      <option value={7}>7 years</option>
                      <option value={10}>10 years</option>
                    </select>
                  </div>
                </div>
              </Section>

              {/* ── Calculated Metrics ─────────────────────────── */}
              <Section title="Calculated Metrics" icon={<Calculator size={14} />}>
                <div className="space-y-1.5">
                  <MetricRow label="IR&D Budget" value={formatCurrency(metrics.irdBudget)} />
                  <MetricRow label="Project Investment" value={formatCurrency(grandTotal)} />
                  <MetricRow label="+ Swim Lane" value={formatCurrency(resourceConfig.swimLaneInvestment)} />
                  <MetricRow label="Grand Total Investment" value={formatCurrency(metrics.totalInvestment)} />
                  <MetricRow label="FTEs Needed" value={metrics.ftesNeeded.toFixed(1)} />
                  <MetricRow label="IWT South FTEs" value={metrics.iwtSouthFtes.toFixed(1)} />
                  <MetricRow label="Other Sub FTEs" value={metrics.otherSubcontractorFtes.toFixed(1)} />
                  <MetricRow label="Total Available FTEs" value={metrics.totalAvailableFtes.toFixed(1)} />
                  <MetricRow
                    label="Resource Delta"
                    value={`${metrics.fteDelta >= 0 ? '+' : ''}${metrics.fteDelta.toFixed(1)} FTEs`}
                    highlight={metrics.fteDelta < 0 ? 'red' : 'green'}
                  />
                </div>
              </Section>

              {/* ── Funded Projects ────────────────────────────── */}
              {fundedNodes.length > 0 && (
                <Section title={`Funded Projects (${fundedNodes.length})`} icon={<DollarSign size={14} />}>
                  <div className="space-y-1 max-h-48 overflow-y-auto">
                    {fundedNodes.map(n => (
                      <div
                        key={n.id}
                        className="flex items-center justify-between text-xs px-2 py-1.5 rounded"
                        style={{ background: 'var(--glass-bg-subtle)' }}
                      >
                        <span className="truncate mr-2" style={{ color: 'var(--text-primary)' }}>
                          {n.name}
                        </span>
                        <span className="shrink-0 tabular-nums" style={{ color: 'var(--text-muted)' }}>
                          {formatCurrency((n.iRnD ?? 0) + (n.fRnD ?? 0))}
                        </span>
                      </div>
                    ))}
                  </div>
                </Section>
              )}
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}

// ── Sub-components ───────────────────────────────────────────────────

function Section({
  title,
  icon,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div
      className="rounded-lg p-4"
      style={{
        background: 'var(--glass-bg-subtle)',
        border: '1px solid var(--glass-border)',
      }}
    >
      <h3
        className="text-xs font-semibold uppercase tracking-wider mb-3 flex items-center gap-1.5"
        style={{ color: 'var(--text-muted)' }}
      >
        {icon}
        {title}
      </h3>
      {children}
    </div>
  );
}

function ConfigInput({
  label,
  value,
  onChange,
  format,
}: {
  label: string;
  value: number;
  onChange: (value: string) => void;
  format: 'currency' | 'percent' | 'number';
}) {
  const displayValue = format === 'currency'
    ? value.toLocaleString()
    : format === 'percent'
      ? value.toFixed(1)
      : value.toString();

  return (
    <div className="flex items-center justify-between gap-3">
      <label className="text-xs shrink-0" style={{ color: 'var(--text-secondary)' }}>
        {label}
      </label>
      <input
        key={displayValue /* WQ-339: re-sync after "Reset to Defaults" */}
        type="text"
        defaultValue={displayValue}
        onBlur={(e) => {
          const raw = e.target.value.replace(/[,$%]/g, '');
          onChange(raw);
        }}
        className="w-32 px-2 py-1 text-xs text-right rounded"
        style={{
          background: 'var(--modal-input-bg)',
          border: '1px solid var(--modal-input-border)',
          color: 'var(--text-primary)',
          outline: 'none',
        }}
      />
    </div>
  );
}

function MetricRow({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string;
  highlight?: 'red' | 'green';
}) {
  const color = highlight === 'red'
    ? '#ef4444'
    : highlight === 'green'
      ? '#22c55e'
      : 'var(--text-primary)';

  return (
    <div className="flex items-center justify-between text-xs">
      <span style={{ color: 'var(--text-muted)' }}>{label}</span>
      <span className="font-medium tabular-nums" style={{ color }}>{value}</span>
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────

function formatCurrency(value: number): string {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(2)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
}
